import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { FasteningChoice } from "../../../../../http/border/types/fasteningChoices";
import { Substructure } from "../../../../../http/border/types/substructures";
import { useFasteningChoicesApi } from "../../../../../http/border/useFasteningChoices";
import { useSubstructuresApi } from "../../../../../http/border/useSubstructures";
import { SearchAdminItem } from "../../../../../http/types/items";
import Button from "../../../../ui/Button";
import Form, { FormChildrenProps } from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import ItemQuantityFieldArray from "../../../../ui/ItemQuanitityField";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

type FasteningChoiceFormBody = {
  substructure: Substructure | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  fastenings: {
    fastening: SearchAdminItem | null;
    factorPerUpright: number | null;
  }[];
};

interface FasteningChoiceFormProps {
  fasteningChoice?: FasteningChoice;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose: () => void;
  systemId: string;
}

const toDefaultValues = (fasteningChoice?: FasteningChoice): FasteningChoiceFormBody => ({
  substructure: fasteningChoice?.substructure ?? null,
  order: fasteningChoice?.order ?? 50,
  visibility: fasteningChoice?.visibility ?? "hidden",
  fastenings: fasteningChoice?.fastenings.map((item) => ({
    fastening: item.item,
    factorPerUpright: item.factorPerUpright,
  })) ?? [{ fastening: null, factorPerUpright: null }],
});

const FasteningChoiceForm: FC<FasteningChoiceFormProps> = ({
  fasteningChoice,
  isDisabled,
  isLoading,
  onClose,
  systemId,
}) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const fasteningChoicesApi = useFasteningChoicesApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const defaultValues = toDefaultValues(fasteningChoice);

  return (
    <Form<FasteningChoiceFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || isDisabled || isLoading}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        setIsSubmitting(true);

        const fastenings = formData.fastenings.map((row) => ({
          itemId: row.fastening?.id ?? "",
          factorPerUpright: row.factorPerUpright!,
        }));

        try {
          if (fasteningChoice?.id) {
            await fasteningChoicesApi.updateAdminFasteningChoice(fasteningChoice.id, {
              systemId,
              substructureId: formData.substructure?.id ?? "",
              order: formData.order!,
              visibility: formData.visibility,
              fastenings,
            });
          } else {
            await fasteningChoicesApi.insertAdminFasteningChoice({
              systemId,
              substructureId: formData.substructure?.id ?? "",
              fastenings,
            });
          }
          onClose();
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
        } finally {
          setIsSubmitting(false);
        }
      }}
    >
      {(formProps) => (
        <FasteningChoiceFields
          {...formProps}
          defaultValues={defaultValues}
          fasteningChoiceId={fasteningChoice?.id}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
        />
      )}
    </Form>
  );
};

type FasteningChoiceFieldsProps = FormChildrenProps<FasteningChoiceFormBody> & {
  defaultValues: FasteningChoiceFormBody;
  fasteningChoiceId?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSubmitting?: boolean;
};

const FasteningChoiceFields: FC<FasteningChoiceFieldsProps> = ({
  control,
  defaultValues,
  fasteningChoiceId,
  getValues,
  isDisabled,
  isLoading,
  isSubmitting,
  reset,
  setValue,
}) => {
  const i18n = useI18n();
  const substructuresApi = useSubstructuresApi();

  const [defaultSubstructureOptions, setDefaultSubstructureOptions] = useState<Substructure[]>([]);

  const substructureSearchTerm = useRef("");
  const substructureAborter = useRef(new AbortController());

  const loadSubstructureOptions = createLoadOptions(
    substructureAborter,
    substructureSearchTerm,
    substructuresApi.getAdminSubstructures,
  );

  useEffect(() => {
    reset(defaultValues);
  }, [fasteningChoiceId]);

  useEffect(() => {
    loadSubstructureOptions(substructureSearchTerm.current).then(setDefaultSubstructureOptions);
  }, []);

  const disabled = isDisabled || isLoading || isSubmitting;

  return (
    <FormFieldsContainer fullWidth>
      <Controller
        name="substructure"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <FormField label={i18n.translation.substructures.singular} error={fieldState.error}>
            {({ labelId }) => (
              <CustomAsyncSelect
                {...field}
                inputId={labelId}
                defaultOptions={defaultSubstructureOptions}
                isDisabled={disabled}
                loadOptions={loadSubstructureOptions}
                getOptionLabel={(x) => x.name}
                getOptionValue={(x) => x.id}
                isInvalid={fieldState.invalid}
                onChange={(value) => setValue("substructure", value ?? null)}
              />
            )}
          </FormField>
        )}
      />

      {!!fasteningChoiceId && (
        <>
          <Controller
            name="visibility"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                {({ labelId }) => (
                  <VisibilityScope
                    {...field}
                    inputId={labelId}
                    isDisabled={disabled}
                    onChange={(value) => setValue("visibility", value)}
                  />
                )}
              </FormField>
            )}
          />
          <Controller
            name="order"
            control={control}
            rules={{ required: true, min: 1 }}
            render={({ field, fieldState }) => (
              <FormField label={i18n.translation.common.order} error={fieldState.error}>
                {({ labelId, isInvalid }) => (
                  <Numeric
                    {...field}
                    id={labelId}
                    isInvalid={isInvalid}
                    decimalScale={0}
                    allowNegative={false}
                    disabled={disabled}
                    onBlur={(value) => setValue("order", value)}
                  />
                )}
              </FormField>
            )}
          />
        </>
      )}

      <ItemQuantityFieldArray
        control={control}
        name="fastenings"
        getValues={getValues}
        disabled={disabled}
        itemLabelText="Item"
        quantityLabelText={i18n.translation.fasteningChoices.factorPerUpright}
        addButtonTitle="Add"
        deleteButtonTitle="Delete"
        itemFieldName="fastening"
        quantityFieldName="factorPerUpright"
      />
    </FormFieldsContainer>
  );
};

export default FasteningChoiceForm;
